import avatarBoris from "@/assets/avatar-boris.jpg";
import avatarAlbert from "@/assets/avatar-albert.jpg";
import avatarMax from "@/assets/avatar-max.jpg";
import avatarSerge from "@/assets/avatar-serge.jpg";
import avatarVlad from "@/assets/avatar-vlad.jpg";

/**
 * TestimonialsWall, single-row auto-scrolling marquee of testimonial
 * cards. Same treatment on desktop and mobile, pauses on hover.
 */


interface Testimonial {
  quote: string;
  name: string;
  role: string;
  company: string;
  avatar: string;
}

const LEFT: Testimonial[] = [
  {
    quote:
      "We run a content shop, reviews, gift guides, comparison pieces. Mane's research and writer agents handle the long-tail work that used to eat our editors' weeks. The Brain remembers our voice and never re-pitches a product we already covered.",
    name: "Boris",
    role: "Founder",
    company: "Soxy",
    avatar: avatarBoris,
  },
  {
    quote:
      "Honest reviews live or die on consistency. We point Mane at a product, it pulls specs, sentiment, and pricing across sources, then drafts in our house style. Editorial still ships. Mane just removes the grind.",
    name: "Albert",
    role: "Editor-in-Chief",
    company: "Honest Brand Reviews",
    avatar: avatarAlbert,
  },
];

const RIGHT: Testimonial[] = [
  {
    quote:
      "We build POS for restaurants, boring on the outside, gnarly on the inside. Mane's engineering pod runs migrations, regressions, and customer ticket triage in parallel. Feels like we hired a second team without onboarding one.",
    name: "Serge",
    role: "CEO",
    company: "Change",
    avatar: avatarSerge,
  },
  {
    quote:
      "Bellenae is a small DTC brand with a loud community. Mane drafts product copy, replies to support, and watches Shopify analytics, all from Slack. The persistent memory is the unlock; every season builds on the last.",
    name: "Vlad",
    role: "Founder",
    company: "Bellenae",
    avatar: avatarVlad,
  },
];

const FEATURED: Testimonial = {
  quote:
    "We deliver full-cycle product work for clients across retail, healthcare, construction, every engagement has its own context, stack, and stakeholders. Mane gives our pods a shared brain across all of it. Mr. Mane routes the request, the right specialist picks it up, and what was learned on one project compounds into the next. It's the closest thing to institutional memory we've ever had.",
  name: "Max",
  role: "CEO",
  company: "ITM House",
  avatar: avatarMax,
};


const Avatar = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    width={36}
    height={36}
    loading="lazy"
    decoding="async"
    className="w-9 h-9 rounded-full object-cover border border-[hsl(var(--border)/0.6)]"
  />
);

const ALL: Testimonial[] = [FEATURED, ...LEFT, ...RIGHT];

const Card = ({ t }: { t: Testimonial }) => (
  <article
    className="shrink-0 w-[88vw] sm:w-[440px] md:w-[480px] bg-card border border-[hsl(var(--border)/0.7)] rounded-[5px] p-7 sm:p-8 flex flex-col gap-6"
    style={{ minHeight: 260 }}
  >
    <span
      className="font-mono text-[10px] uppercase tracking-[0.22em]"
      style={{ color: "hsl(var(--foreground) / 0.55)" }}
    >
      {t.company}
    </span>
    <p
      className="text-[15px] sm:text-[16px] leading-relaxed flex-1"
      style={{ color: "hsl(var(--foreground) / 0.92)" }}
    >
      "{t.quote}"
    </p>
    <div className="flex items-center gap-3 pt-2 border-t border-[hsl(var(--border)/0.5)]">
      <Avatar src={t.avatar} alt={`${t.name}, ${t.role} at ${t.company}`} />
      <div className="flex flex-col leading-tight">
        <span className="text-[14px] text-foreground">{t.name}</span>
        <span
          className="text-[12px]"
          style={{ color: "hsl(var(--foreground) / 0.6)" }}
        >
          {t.role}, {t.company}
        </span>
      </div>
    </div>
  </article>
);

import SectionHeader from "@/components/SectionHeader";

const TestimonialsWall = () => (
  <section className="pt-12 pb-20 md:pt-16 md:pb-28 overflow-hidden">
    <div className="max-w-content mx-auto px-5 sm:px-8">
      <SectionHeader
        eyebrow="Love wall"
        title={<>Teams that ship <em>with Mane every day.</em></>}
        subtitle="Founders, editors, and operators using Mane to run real work, not demos."
      />
    </div>

    <style>{`
      @keyframes testimonials-marquee {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .testimonials-track { animation: testimonials-marquee 90s linear infinite; }
      .testimonials-track:hover { animation-play-state: paused; }
      @media (prefers-reduced-motion: reduce) {
        .testimonials-track { animation: none; }
      }
    `}</style>

    <div
      className="relative mt-4"
      style={{
        maskImage:
          "linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0, black 4%, black 96%, transparent 100%)",
      }}
    >
      <div className="testimonials-track flex items-stretch gap-5 sm:gap-6 w-max">
        {[...ALL, ...ALL].map((t, i) => (
          <Card key={`${t.company}-${i}`} t={t} />
        ))}
      </div>
    </div>
  </section>
);

export default TestimonialsWall;
